import React, { useState } from "react";
import { Play, Lock } from "lucide-react";
import { caseTextures, stickerPalette } from "../mock/enrich";

// Renders a single 3D VHS tape case (pure CSS, no images).
// Each tape gets a unique sticker, tilt, wear pattern from enrich.js.
const VHSTape = ({ tape, index, total, onClick, locked = false }) => {
  const [hover, setHover] = useState(false);

  const stagger = ((index % 3) - 1) * 4;
  const tx = caseTextures[tape.id] || { tilt: 0, sticker: null, wear: 0.4, corner: "tl" };
  const stickerTone = tx.sticker ? stickerPalette[tx.sticker.tone] || stickerPalette.cream : null;

  // Deterministic wear scratches based on tape id
  const seed = (tape.id.charCodeAt(0) + tape.id.charCodeAt(1)) % 5;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="tape group relative outline-none focus-visible:ring-2 focus-visible:ring-[#f5b14e] rounded-[3px]"
      style={{
        width: "clamp(74px, 8vw, 112px)",
        height: "clamp(240px, 26vw, 360px)",
        transform: `translateY(${stagger}px) rotate(${tx.tilt}deg)`,
        zIndex: hover ? 40 : 10 + index,
      }}
      aria-label={`Play ${tape.title}`}
    >
      {/* Cast shadow */}
      <span
        aria-hidden
        className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[85%] h-3 rounded-full blur-md opacity-70"
        style={{ background: "rgba(0,0,0,0.6)" }}
      />

      {/* Tape body */}
      <div
        className="relative w-full h-full rounded-[3px] overflow-hidden vhs-ribs"
        style={{
          background: `linear-gradient(180deg, ${tape.body} 0%, #06040a 100%)`,
          boxShadow: `
            inset 0 0 0 1px rgba(255,255,255,0.05),
            inset -6px 0 12px rgba(0,0,0,0.55),
            inset 6px 0 12px rgba(255,255,255,0.03),
            0 12px 30px rgba(0,0,0,0.6)
          `,
        }}
      >
        {/* Colored spine label */}
        <div
          className="absolute inset-x-0 top-0 h-[42%] flex flex-col justify-between px-2 py-3"
          style={{
            background: `linear-gradient(180deg, ${tape.spine} 0%, ${tape.spine} 78%, ${tape.stripe} 100%)`,
            boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.35)",
          }}
        >
          <div className="flex items-center justify-between">
            <span className="text-[7px] mono uppercase tracking-widest" style={{ color: tape.body }}>
              VHS &bull; {tape.year}
            </span>
            <span className="text-[7px] mono" style={{ color: tape.body }}>
              SP
            </span>
          </div>

          <div className="flex-1 flex items-center justify-center">
            <div
              className="font-serif font-medium text-center px-1 leading-[1.05]"
              style={{
                color: tape.body,
                fontFamily: "'Instrument Serif', serif",
                fontSize: "clamp(11px, 1.3vw, 18px)",
                textShadow: "0 1px 0 rgba(255,255,255,0.15)",
              }}
            >
              {tape.title}
            </div>
          </div>

          <div
            className="text-[7px] mono uppercase tracking-widest text-center"
            style={{ color: tape.body, opacity: 0.75 }}
          >
            {tape.subtitle}
          </div>
        </div>

        {/* Diagonal wear scratches on the spine */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-[42%] pointer-events-none mix-blend-overlay"
          style={{
            opacity: tx.wear,
            background: `repeating-linear-gradient(${105 + seed * 8}deg, transparent 0 ${
              20 + seed * 4
            }px, rgba(0,0,0,0.18) ${20 + seed * 4}px ${21 + seed * 4}px)`,
          }}
        />

        {/* Middle plastic band */}
        <div
          className="absolute inset-x-0"
          style={{
            top: "42%",
            height: "4%",
            background: `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(255,255,255,0.04))`,
          }}
        />

        {/* Lower body */}
        <div
          className="absolute inset-x-0 bottom-0 h-[54%] px-2 py-3 flex flex-col justify-between"
          style={{
            background: `linear-gradient(180deg, ${tape.body} 0%, #05030a 100%)`,
          }}
        >
          <div>
            <div
              className="text-[7px] mono uppercase tracking-widest"
              style={{ color: tape.accent, opacity: 0.9 }}
            >
              {tape.category}
            </div>
            <div className="mt-1 text-[8px] mono" style={{ color: "#f2ece1", opacity: 0.7 }}>
              {tape.runtime}
            </div>
          </div>

          <div className="space-y-1.5">
            <div
              className="h-[14%] w-full rounded-[1px]"
              style={{
                background: `repeating-linear-gradient(90deg, ${tape.accent}22 0 6px, transparent 6px 10px)`,
              }}
            />
            <div className="h-2 barcode rounded-[1px] bg-[#f2ece1]" />
            <div
              className="text-[6px] mono text-center"
              style={{ color: "#f2ece1", opacity: 0.55 }}
            >
              CAT #{tape.id.toUpperCase()}-{tape.year}
            </div>
          </div>
        </div>

        {/* Sticker overlay \u2014 slapped on a corner at a small angle */}
        {tx.sticker && stickerTone && (
          <div
            aria-hidden
            className="absolute z-20 mono uppercase font-semibold shadow-md"
            style={{
              [tx.corner.includes("t") ? "top" : "bottom"]: `${tx.sticker.offset}px`,
              [tx.corner.includes("l") ? "left" : "right"]: `-${tx.sticker.offset - 2}px`,
              transform: `rotate(${
                tx.corner === "tl" ? -12 : tx.corner === "tr" ? 10 : tx.corner === "bl" ? -8 : 14
              }deg)`,
              background: stickerTone.bg,
              color: stickerTone.text,
              padding: "3px 6px",
              fontSize: "6px",
              letterSpacing: "0.08em",
              borderRadius: "1px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
              opacity: 0.95,
            }}
          >
            {tx.sticker.label}
          </div>
        )}

        {/* Right edge highlight */}
        <div
          aria-hidden
          className="absolute right-0 top-0 h-full w-[3px]"
          style={{
            background: "linear-gradient(90deg, rgba(255,255,255,0.14), rgba(0,0,0,0.55))",
          }}
        />
        <div
          aria-hidden
          className="absolute left-0 top-0 h-full w-[3px]"
          style={{
            background: "linear-gradient(90deg, rgba(0,0,0,0.65), rgba(255,255,255,0.05))",
          }}
        />

        {/* Locked overlay for vault tapes */}
        {locked && (
          <div
            className="absolute inset-0 flex items-center justify-center backdrop-blur-[4px]"
            style={{ background: "rgba(6,4,4,0.55)" }}
          >
            <div className="flex flex-col items-center gap-1 text-[#f2ece1]">
              <Lock className="w-5 h-5" />
              <span className="text-[8px] mono uppercase tracking-widest opacity-70">
                Locked
              </span>
            </div>
          </div>
        )}

        {/* Play overlay on hover (only when unlocked) */}
        {!locked && (
          <div
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
              hover ? "opacity-100" : "opacity-0"
            }`}
            style={{
              background:
                "radial-gradient(circle at 50% 50%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)",
            }}
          >
            <div
              className="flex items-center justify-center rounded-full backdrop-blur-sm"
              style={{
                width: "clamp(34px, 3.4vw, 48px)",
                height: "clamp(34px, 3.4vw, 48px)",
                background: "rgba(242,236,225,0.92)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              }}
            >
              <Play className="w-4 h-4 text-[#0e0b09] fill-[#0e0b09] ml-0.5" />
            </div>
          </div>
        )}
      </div>

      {/* Floating label */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 -bottom-14 whitespace-nowrap text-[11px] mono uppercase tracking-widest text-[#f2ece1] transition-all duration-300 ${
          hover ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
        }`}
      >
        <span style={{ color: tape.accent }}>&#9679;</span> {tape.title}
      </div>
    </button>
  );
};

export default VHSTape;
